import { Trans, useLingui } from "@lingui/react/macro";
import { useForm } from "react-hook-form";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import Select from "@medoma/components/Select/Select";
import { FilledButton } from "@medoma/components/Button/Button";
import {
  useEnabledCompetences,
  createShift,
  NO_EMPLOYEE,
  shiftKeys,
  type INewShift,
} from "@/api/Shifts";
import { useEmployees } from "@/api/Employee";
import {
  compareEmployeesByCompetence,
  employeeName,
  medicalCompetenceDictionary,
  shiftTypesDictionary,
  type IMedicalCompetence,
  type IShiftType,
} from "@medoma/models/shifts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelectedDate } from "@/Utils/useSelectedDate";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { addDays } from "date-fns";
import { humanFormat } from "@medoma/models/date-and-time";
import { setTimeOnDate } from "@/Utils/DateUtils";
import { useTeams } from "@/api/Patients";
import styles from "./NewCustomShiftForm.module.scss";
import { z } from "zod";
import { shiftTypesByOrganization } from "@medoma/models/shiftTypesByOrganization";
import { useUserProfile } from "@/api/Users";

const NO_TEAM = 0;
const NO_SHIFT_TYPE = "";
const NO_COMPETENCE = "";

const startDaySchema = z.enum(["previousDay", "selectedDate"]);
const endDaySchema = z.enum(["selectedDate", "nextDay"]);

type NewShiftFormValues = {
  startTime: string;
  startDay: z.infer<typeof startDaySchema>;
  endTime: string;
  endDay: z.infer<typeof endDaySchema>;
  competence: IMedicalCompetence;
  employeeId: number | null;
  teamId: number | null;
  shiftType: IShiftType | null | typeof NO_SHIFT_TYPE;
};

const NewCustomShiftForm = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const selectedDate = useSelectedDate();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { organization } = useUserProfile();

  const formatWeekday = (date: Date) => {
    const weekday = humanFormat(date, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  };

  const {
    data: competences,
    isPending: competencesPending,
    isError: competencesError,
  } = useEnabledCompetences();
  const {
    data: employees,
    isPending: employeesPending,
    isError: employeesError,
  } = useEmployees();
  const {
    data: teams,
    isPending: teamsPending,
    isError: teamsError,
  } = useTeams();

  const shiftTypes = shiftTypesByOrganization[organization];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewShiftFormValues>({
    defaultValues: {
      startDay: startDaySchema.enum.selectedDate,
      endDay: endDaySchema.enum.selectedDate,
      employeeId: NO_EMPLOYEE,
      teamId: NO_TEAM,
      shiftType: NO_SHIFT_TYPE,
    },
  });

  const { mutate: createShiftMutation, isPending: isCreatingShift } =
    useMutation({
      mutationFn: (newShift: INewShift) => createShift(newShift),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: shiftKeys.all });
        reset();
      },
      onError: (error) => {
        displayErrorMessageAlert(deducedError(error));
      },
    });

  const validateAndSubmit = handleSubmit((formData) => {
    const {
      startTime,
      startDay,
      endTime,
      endDay,
      competence,
      employeeId,
      teamId,
      shiftType,
    } = formData;

    const newShift: INewShift = {
      startDateTime: setTimeOnDate({
        selectedDate,
        time: startTime,
        addedDays: startDay === startDaySchema.enum.previousDay ? -1 : 0,
      }).toISOString(),
      endDateTime: setTimeOnDate({
        selectedDate,
        time: endTime,
        addedDays: endDay === endDaySchema.enum.nextDay ? 1 : 0,
      }).toISOString(),
      competence,
      employeeId: employeeId === NO_EMPLOYEE ? null : employeeId,
      teamId: teamId === NO_TEAM ? null : teamId,
      shiftType: shiftType || null,
    };

    createShiftMutation(newShift);
  });

  if (competencesPending || employeesPending || teamsPending) {
    return <Loading message={t`Hämtar information om medarbetare`} />;
  }

  if (competencesError || employeesError || teamsError) {
    return (
      <ErrorMessage
        message={t`Kunde inte hämta information om medarbetare. Prova att uppdatera sidan. Kontakta support om felet kvarstår.`}
      />
    );
  }

  const sortedEmployees = [...employees].sort(compareEmployeesByCompetence);

  return (
    <div className={styles.formWrapper}>
      <Form onSubmit={validateAndSubmit}>
        <Form.Row type="flex">
          <InputField
            {...register("startTime", {
              required: {
                value: true,
                message: t`Ange en starttid`,
              },
            })}
            label={t`Starttid`}
            showOptionalLabel={false}
            type="time"
            errorMessage={errors.startTime?.message}
          />
          <Select {...register("startDay", { required: true })} label={t`Dag`}>
            <option value={startDaySchema.enum.previousDay}>
              {formatWeekday(addDays(selectedDate, -1))}
            </option>
            <option value={startDaySchema.enum.selectedDate}>
              {formatWeekday(selectedDate)}
            </option>
          </Select>
        </Form.Row>

        <Form.Row type="flex">
          <InputField
            {...register("endTime", {
              required: {
                value: true,
                message: t`Ange en sluttid`,
              },
            })}
            label={t`Sluttid`}
            showOptionalLabel={false}
            type="time"
            errorMessage={errors.endTime?.message}
          />
          <Select {...register("endDay", { required: true })} label={t`Dag`}>
            <option value={endDaySchema.enum.selectedDate}>
              {formatWeekday(selectedDate)}
            </option>
            <option value={endDaySchema.enum.nextDay}>
              {formatWeekday(addDays(selectedDate, 1))}
            </option>
          </Select>
        </Form.Row>

        <Form.Row>
          <Select
            {...register("competence", {
              required: {
                value: true,
                message: t`Välj en kompetens`,
              },
            })}
            label={t`Kompetens`}
            errorMessage={errors.competence?.message}
          >
            <option value={NO_COMPETENCE}>
              <Trans>Välj kompetens</Trans>
            </option>
            {competences.map((competence) => (
              <option key={competence} value={competence}>
                {t(medicalCompetenceDictionary[competence].long)}
              </option>
            ))}
          </Select>
        </Form.Row>

        <Form.Row>
          <Select
            {...register("employeeId", {
              valueAsNumber: true,
            })}
            label={t`Medarbetare`}
          >
            <option value={NO_EMPLOYEE}>
              <Trans>Ingen</Trans>
            </option>
            {sortedEmployees.map(
              ({ id, firstName, lastName, competence, distinctionLabel }) => (
                <option key={id} value={id}>
                  {employeeName(
                    { firstName, lastName },
                    { employeeNameFormat: "full" },
                  )}
                  {competence
                    ? `, ${t(medicalCompetenceDictionary[competence].long)}`
                    : null}
                  {distinctionLabel ? ` (${distinctionLabel})` : null}
                </option>
              ),
            )}
          </Select>
        </Form.Row>

        {teams.length > 0 ? (
          <Form.Row>
            <Select
              {...register("teamId", {
                valueAsNumber: true,
              })}
              label={t`Team`}
            >
              <option value={NO_TEAM}>
                <Trans>Inget</Trans>
              </option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.displayName}
                </option>
              ))}
            </Select>
          </Form.Row>
        ) : null}

        {shiftTypes.length > 0 ? (
          <Form.Row>
            <Select {...register("shiftType")} label={t`Typ`}>
              <option value={NO_SHIFT_TYPE}>
                <Trans>Ingen</Trans>
              </option>
              {shiftTypes.map((type) => (
                <option key={type} value={type}>
                  {t(shiftTypesDictionary[type])}
                </option>
              ))}
            </Select>
          </Form.Row>
        ) : null}

        <Form.Row type="flex">
          <FilledButton type="submit" disabled={isCreatingShift}>
            <Trans>Skapa arbetspass</Trans>
          </FilledButton>
        </Form.Row>
      </Form>
    </div>
  );
};

export default NewCustomShiftForm;
